import * as Dialog from "@radix-ui/react-dialog";
import { LoaderCircle, X } from "lucide-react";
import { forwardRef, useEffect, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type MouseEvent, type ReactNode } from "react";
import { clsx } from "clsx";
import "./primitives.css";

/** 确认件专用 shadcn 按钮，尺寸和语义变体不影响其他业务页面。 */
export const ConfirmationButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "default" | "outline" | "ghost" | "destructive";
  size?: "default" | "sm" | "icon";
  loading?: boolean;
}>(function ConfirmationButton({ variant = "outline", size = "default", type = "button", loading, disabled, className, children, ...props }, ref) {
  return <button {...props} ref={ref} type={type} className={clsx("confirmation-button", className)} data-slot="button" data-variant={variant} data-size={size} disabled={disabled || loading} aria-busy={loading || undefined}>
    {loading ? <LoaderCircle data-icon="inline-start" className="confirmation-spinner" aria-hidden /> : null}{children}
  </button>;
});

/** 确认件专用输入框，保留原生表单与可访问性属性。 */
export const ConfirmationInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function ConfirmationInput({ className, ...props }, ref) {
  return <input {...props} ref={ref} data-slot="input" className={clsx("confirmation-input", className)} />;
});

/** 使用 Radix 管理焦点、Esc 和遮罩的确认件专用侧滑面板。 */
export function ConfirmationSheet({ open, onClose, title, description, children, footer, className, keepMounted = false }: {
  open: boolean; onClose: () => void; title: string; description?: ReactNode; children: ReactNode; footer?: ReactNode; className?: string; keepMounted?: boolean;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const closeFromOverlay = (event: MouseEvent<HTMLDivElement>) => {
    // 只响应遮罩本身；浮层交互仍保留，关闭权限由宿主的草稿和写入保护决定。
    if (open && event.target === event.currentTarget) onClose();
  };
  useEffect(() => {
    if (!keepMounted || !open) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    contentRef.current?.focus();
    return () => { document.body.style.overflow = overflow; previousFocus?.focus({ preventScroll: true }); };
  }, [keepMounted, open]);
  return <Dialog.Root open={open} modal={!keepMounted} onOpenChange={value => { if (!value) onClose(); }}>
    <Dialog.Portal forceMount={keepMounted ? true : undefined}>{keepMounted ? <div hidden={!open} className="confirmation-editor-theme confirmation-overlay" onClick={closeFromOverlay} /> : <Dialog.Overlay className="confirmation-editor-theme confirmation-overlay" onClick={closeFromOverlay} />}
      <Dialog.Content ref={contentRef} forceMount={keepMounted ? true : undefined} hidden={!open} aria-modal={open} className={clsx("confirmation-editor-theme confirmation-sheet", className)} aria-describedby={undefined} onOpenAutoFocus={event => { if (!open) event.preventDefault(); }} onInteractOutside={event => event.preventDefault()} onEscapeKeyDown={event => {
        const nestedDialog = event.target instanceof Element ? event.target.closest('[role="dialog"]') : null;
        // 内层弹窗自行处理 Esc；外层只阻止自身关闭，不拦截事件传播。
        if (!open || (nestedDialog && nestedDialog !== contentRef.current && contentRef.current?.contains(nestedDialog))) event.preventDefault();
      }} onKeyDown={event => {
        if (!keepMounted || !open || event.key !== "Tab") return;
        const controls = [...event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]')].filter(element => element.getClientRects().length);
        const first = controls[0], last = controls.at(-1);
        if (event.shiftKey && (document.activeElement === first || document.activeElement === event.currentTarget)) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }}>
        <header className="confirmation-sheet-header"><div><Dialog.Title>{title}</Dialog.Title>{description ? <Dialog.Description asChild><div className="confirmation-sheet-description">{description}</div></Dialog.Description> : null}</div>
          <Dialog.Close asChild><ConfirmationButton variant="ghost" size="icon" aria-label={`关闭${title}`}><X aria-hidden /></ConfirmationButton></Dialog.Close>
        </header>
        <div className="confirmation-sheet-body">{children}</div>
        {footer ? <footer className="confirmation-sheet-footer">{footer}</footer> : null}
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>;
}
